import { passwordRegex } from 'constants/password';
import i18n, { FALLBACK_LANGUAGE } from 'i18n';
import {
  apiClient,
  InvitationAlreadyAcceptedError,
  type UserApi,
} from 'modules/api-client';
import { useAuth } from 'modules/auth/AuthContext';
import {
  BaseCard,
  Button,
  FormField,
  HibooIcon,
  NavigationLink,
  TextInput,
} from 'modules/design-system';
import { PrivacyPolicyContainer } from 'modules/signup/common';
import { useTracking } from 'modules/utils';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { AuthBaseCardHeader } from '../../components/AuthBaseCardHeader/AuthBaseCardHeader';
import { AuthLayout } from '../../components/AuthLayout/AuthLayout';
import { ErrorMessage, Footer, Form } from '../index.css';

const HelperText = styled.div`
  color: ${({ theme }) => theme.colors.text.subtle};
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
`;

export const AcceptInvitationPage = () => {
  const { actions } = useAuth();
  const { track } = useTracking<string>();
  const navigate = useNavigate();
  const [t] = useTranslation('user');
  const theme = useTheme();

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    password: '',
  });
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [organization, setOrganization] = useState('');

  const urlParams = new URLSearchParams(window.location.search);
  const invitationKey = urlParams.get('invitationKey') || '';
  const language = i18n.resolvedLanguage ?? FALLBACK_LANGUAGE;

  const onAcceptInvitationSuccess = (user: UserApi) => {
    const { email, firstName, lastName, organizationId, organizationName } =
      user;

    track('Signed Up', {
      type: 'Invited',
      first_name: firstName,
      last_name: lastName,
      email,
    });
    analytics.group(`${organizationId}`, {
      organizationName,
    });

    user.signUpInProgress = true;

    actions.login(user);
    setPending(false);

    navigate('/add-user-job');
  };

  useEffect(() => {
    apiClient.user
      .getInvitation({
        invitationKey,
      })
      .then((invitation) => {
        setOrganization(invitation.organizationName);
      })
      .catch((err: Error) => {
        setError(
          err instanceof InvitationAlreadyAcceptedError
            ? t('user.acceptInvitation.alreadyAccepted')
            : err.message,
        );
      });
  }, [invitationKey, t]);

  const submitForm = () => {
    if (form.firstName && form.lastName && form.password && !pending) {
      setPending(true);
      apiClient.user
        .acceptInvitation({
          invitationKey,
          firstName: form.firstName,
          lastName: form.lastName,
          password: form.password,
          language,
        })
        .then((user) => {
          onAcceptInvitationSuccess(user);
        })
        .catch((err: Error) => {
          setError(err.message);
          setPending(false);
        });
    }
  };

  return (
    <AuthLayout>
      <BaseCard>
        <AuthBaseCardHeader
          title={t('user.acceptInvitation.header', { orgName: organization })}
        />
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            submitForm();
          }}
        >
          <FormField
            label={t('user.acceptInvitation.form.firstName.label')}
            htmlFor="invitation-firstName"
          >
            <TextInput
              fullWidth
              id="invitation-firstName"
              value={form.firstName}
              onChange={(e) => setForm({ ...form, firstName: e.target.value })}
              placeholder={t(
                'user.acceptInvitation.form.firstName.placeholder',
              )}
              autoComplete="off"
            />
          </FormField>

          <FormField
            label={t('user.acceptInvitation.form.lastName.label')}
            htmlFor="invitation-lastName"
          >
            <TextInput
              fullWidth
              id="invitation-lastName"
              value={form.lastName}
              onChange={(e) => setForm({ ...form, lastName: e.target.value })}
              placeholder={t('user.acceptInvitation.form.lastName.placeholder')}
              autoComplete="off"
            />
          </FormField>

          <FormField
            label={t('user.acceptInvitation.form.password.label')}
            htmlFor="invitation-password"
          >
            <TextInput
              type="password"
              fullWidth
              id="invitation-password"
              value={form.password}
              pattern={passwordRegex.source}
              title={t('user.resetPassword.form.help')}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder={t('user.acceptInvitation.form.password.placeholder')}
              autoComplete="off"
            />
          </FormField>

          <HelperText>{t('user.resetPassword.form.help')}</HelperText>
          <Button
            variant="outline-primary"
            type="submit"
            block
            disabled={
              !form.firstName || !form.lastName || !form.password || pending
            }
          >
            {t('user.acceptInvitation.form.submit')}
          </Button>

          {error && <ErrorMessage>{error}</ErrorMessage>}
        </Form>

        <Footer>
          <NavigationLink
            to="https://www.hiboo.io"
            target="_blank"
            iconLeft={<HibooIcon small color={theme.colors.text.subtle} />}
          >
            {t('whatsHiboo')}
          </NavigationLink>
        </Footer>
      </BaseCard>

      <PrivacyPolicyContainer>
        <Trans
          i18nKey="user:user.acceptInvitation.form.termsOfUse"
          components={{
            1: (
              // biome-ignore lint/a11y/noAmbiguousAnchorText: link text is provided by the i18n Trans interpolation
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={t('user.acceptInvitation.form.links.termsOfUse')}
              >
                Link
              </a>
            ),
            2: (
              // biome-ignore lint/a11y/noAmbiguousAnchorText: link text is provided by the i18n Trans interpolation
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={t('user.acceptInvitation.form.links.privacyPolicy')}
              >
                Link
              </a>
            ),
          }}
        />
      </PrivacyPolicyContainer>
    </AuthLayout>
  );
};
